import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryKeys } from './keys.js';
import { useInventoryClient } from './provider.js';
import { useIdempotentMutation } from './useIdempotentMutation.js';
/**
 * A posting changes the ledger, the balance projection AND availability — the last one
 * because `issuable` is computed from the same balance row the movement just moved. A
 * mutation that invalidated only the movement list would leave a picker looking at a stale
 * availability number, which is the number they are about to act on.
 */
function invalidateAfterPosting(queryClient) {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.stock.all() });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.reservations.all() });
}
export function useMovements(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.stock.movements(filter),
        queryFn: ({ signal }) => client.stock.listMovements(filter, signal),
        ...options,
    });
}
export function useMovement(movementId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.stock.movement(movementId ?? ''),
        queryFn: ({ signal }) => client.stock.getMovement(movementId, signal),
        enabled: Boolean(movementId),
        ...options,
    });
}
export function useBalances(filter = {}, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.stock.balances(filter),
        queryFn: ({ signal }) => client.stock.listBalances(filter, signal),
        ...options,
    });
}
export function useBalance(params, options) {
    const client = useInventoryClient();
    const ready = Boolean(params.item_id && params.warehouse_id);
    const resolved = {
        item_id: params.item_id ?? '',
        warehouse_id: params.warehouse_id ?? '',
        ...(params.stock_state ? { stock_state: params.stock_state } : {}),
    };
    return useQuery({
        queryKey: inventoryKeys.stock.balance(resolved),
        queryFn: ({ signal }) => client.stock.getBalance(resolved, signal),
        enabled: ready,
        ...options,
    });
}
/** Stock in. The only posting shape that may carry a cost, and it is a per-unit rate. */
export function useReceiveStock() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.stock.receive(input, operation),
        onSuccess: () => invalidateAfterPosting(queryClient),
    });
}
/**
 * Stock out.
 *
 * Handle the two 409s separately in the UI. `InsufficientStockError` means go and find
 * more; `ReservationRefusedError` means the stock is there and promised elsewhere, and the
 * remedy is to cite the hold (`reservation_id`) or release it — not to look for stock.
 */
export function useIssueStock() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.stock.issue(input, operation),
        onSuccess: () => invalidateAfterPosting(queryClient),
    });
}
export function useAdjustStock() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.stock.adjust(input, operation),
        onSuccess: () => invalidateAfterPosting(queryClient),
    });
}
/** The only correction. A movement can be reversed at most once. */
export function useReverseMovement(movementId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.stock.reverse(movementId, input, operation),
        onSuccess: () => invalidateAfterPosting(queryClient),
    });
}
/**
 * Audit the projection against the ledger.
 *
 * `{ apply: false }` reports and changes nothing. A non-empty `discrepancies` is a defect,
 * not maintenance — say so in the interface rather than offering a reassuring "fix".
 */
export function useRebuildBalances() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.stock.rebuildBalances(input.apply, operation),
        onSuccess: (_result, variables) => {
            if (variables.input.apply)
                invalidateAfterPosting(queryClient);
        },
    });
}
//# sourceMappingURL=stock.js.map